import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { Lock, ShieldCheck, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import BookingItemDetails from "@/components/site/BookingItemDetails";

export default function Checkout() {
  const { bookingId } = useParams();
  const [sp] = useSearchParams();
  const cancelled = sp.get("cancelled");
  const { user, loading: authLoading } = useAuth();
  const { format: fmt, currency: dispCcy } = useCurrency();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  // Pre-book re-price confirmation modal state. The backend returns HTTP 409
  // `{code:"price_changed"}` when the fresh AirPriceReq total differs from
  // `booking.total_amount` by more than the server-side threshold ($2). We
  // pause the flow, show the customer old-vs-new, and let them decide before
  // Stripe charges the card.
  const [priceChange, setPriceChange] = useState(null); // {old_total,new_total,currency,delta,direction}

  const submitCheckout = async (payload) => {
    const { data } = await api.post("/payments/checkout", payload);
    window.location.href = data.url;
  };

  const pay = async (overrides = {}) => {
    setLoading(true);
    try {
      await submitCheckout({
        booking_id: bookingId,
        origin_url: window.location.origin,
        ...overrides,
      });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // 409 → structured price-change response. Detail is our object.
      if (status === 409 && detail && typeof detail === "object") {
        if (detail.code === "price_changed" || detail.code === "price_changed_again") {
          setPriceChange(detail);
          setLoading(false);
          return;
        }
        if (detail.code === "no_fares") {
          toast.error(detail.message || "The airline has withdrawn this fare.");
          setLoading(false);
          return;
        }
      }
      toast.error(
        (detail && (typeof detail === "string" ? detail : detail.message)) ||
        "Payment init failed"
      );
      setLoading(false);
    }
  };

  const confirmNewPrice = async () => {
    if (!priceChange) return;
    // User accepted the new price. Retry the checkout with `price_confirmed`
    // + `expected_total` — the backend cross-checks these against a *fresh*
    // AirPrice to ensure the customer isn't locked in on a stale old price
    // (anti-lock-old-price guard).
    const expected = Number(priceChange.new_total);
    setPriceChange(null);
    await pay({ price_confirmed: true, expected_total: expected });
  };

  const applyPromo = async () => {
    setPromoBusy(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode, amount: booking.total_amount });
      // Re-create booking? Simpler: locally show discount and pass code at re-create. Since the booking is already created,
      // we can't mutate amount here without backend support. We'll instead store promo on the booking via PATCH approach
      // (re-create flow would be heavy). For now we adjust the displayed total and pass code to checkout endpoint by
      // recreating? -> Instead, update booking via a lightweight endpoint by recreating: use admin patch via add-on?
      // Simpler approach: add promo at booking creation. Inform user it must be entered in flight review.
      toast.success(`Promo would save $${data.discount}. Tip: apply promo on the review page next time to use it on this booking.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid promo"); }
    finally { setPromoBusy(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        setBooking(data);
        setContact({ name: data.contact?.name || user.name, email: data.contact?.email || user.email, phone: data.contact?.phone || "" });
      } catch (_e) { toast.error("Booking not found"); navigate("/"); }
    })();
  }, [bookingId, user, authLoading, navigate]);

  if (!booking) return <div className="max-w-5xl mx-auto px-6 py-16 text-center text-slate-500">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Checkout</h1>
      {cancelled && (
        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          Your payment was cancelled. You can retry below.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Card className="lg:col-span-2 p-6">
          <h2 className="font-heading text-xl font-semibold text-[var(--brand-accent)]">Contact details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div><Label>Name</Label><Input data-testid="checkout-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="checkout-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Phone</Label><Input data-testid="checkout-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} /></div>
          </div>

          <h2 className="font-heading text-xl font-semibold text-[var(--brand-accent)] mt-8">Booking summary</h2>
          <div className="mt-3 p-4 rounded-xl bg-teal-50/30 border border-teal-100">
            <BookingItemDetails bookingType={booking.booking_type} item={booking.item} travelers={booking.travelers} />
          </div>

          {/* Promo code */}
          <div className="mt-6 p-4 rounded-xl border border-slate-200">
            <Label className="text-sm font-medium text-[var(--brand-accent)]">Promo code</Label>
            {booking.promo_applied ? (
              <div className="mt-2 text-sm flex items-center gap-2">
                <span className="font-mono bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-bold">{booking.promo_applied.code}</span>
                <span className="text-emerald-700">−{fmt(booking.promo_applied.discount)} applied</span>
              </div>
            ) : (
              <div className="mt-2 flex gap-2">
                <Input data-testid="checkout-promo-input" placeholder="e.g. SUMMER10" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} />
                <Button data-testid="checkout-promo-apply" type="button" variant="outline" disabled={promoBusy || !promoCode} onClick={applyPromo}>{promoBusy ? "…" : "Apply"}</Button>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6 h-fit sticky top-20">
          <h3 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Payment summary</h3>
          <div className="mt-4 space-y-2 text-sm">
            {booking.promo_applied ? (
              <>
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmt(Number(booking.original_amount || booking.total_amount))}</span></div>
                <div className="flex justify-between items-center bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 text-emerald-700">
                  <span className="font-mono font-bold text-xs">{booking.promo_applied.code}</span>
                  <span className="text-xs">applied on base fare</span>
                  <span className="font-medium ml-auto">−{fmt(Number(booking.promo_applied.discount))}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmt(booking.total_amount)}</span></div>
            )}
            <div className="flex justify-between text-slate-600"><span>Taxes & fees</span><span>Included</span></div>
            <div className="border-t border-slate-200 my-2" />
            <div className="flex justify-between font-heading text-lg font-bold text-[var(--brand-accent)]">
              <span>Total</span><span data-testid="checkout-total">{fmt(booking.total_amount)} {dispCcy}</span>
            </div>
            {dispCcy !== "USD" && (
              <div className="text-[10px] text-slate-400 leading-snug pt-1">
                Charged via Stripe in USD (${Number(booking.total_amount).toFixed(2)}). Your bank applies its own conversion to your card statement.
              </div>
            )}
          </div>
          <Button data-testid="checkout-pay-btn" disabled={loading} onClick={() => pay()}
                  className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full h-12 mt-5 text-base">
            <Lock className="w-4 h-4 mr-2" /> {loading ? "Redirecting..." : "Pay with Stripe"}
          </Button>
          <p className="text-xs text-slate-500 mt-3 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Secured by Stripe · Test mode
          </p>
        </Card>
      </div>

      {/* Pre-book price-change modal. Shown when the backend detects a fresh
          AirPrice total that differs from booking.total_amount by > $2. */}
      <Dialog open={!!priceChange} onOpenChange={(o) => { if (!o) setPriceChange(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="price-change-modal">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                ${priceChange?.direction === "decrease" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {priceChange?.direction === "decrease"
                  ? <TrendingDown className="w-5 h-5" />
                  : <TrendingUp className="w-5 h-5" />}
              </div>
              <div>
                <DialogTitle className="text-lg">
                  {priceChange?.code === "price_changed_again" ? "The fare changed again" : "The fare has changed"}
                </DialogTitle>
                <DialogDescription className="mt-1">
                  {priceChange?.direction === "decrease"
                    ? "Good news — the airline dropped the price while you were on this page."
                    : "The airline updated the fare since you started checkout. Confirm the new total before we charge your card."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {priceChange && (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="price-change-diff">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-slate-500">Previous total</span>
                <span className="text-sm font-medium text-slate-500 line-through">
                  {priceChange.currency} {Number(priceChange.old_total).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
                <span className="text-sm font-medium text-[var(--brand-accent)]">New total</span>
                <span className="text-lg font-bold text-[var(--brand-accent)]" data-testid="price-change-new-total">
                  {priceChange.currency} {Number(priceChange.new_total).toFixed(2)}
                </span>
              </div>
              <div className={`flex items-center justify-between px-4 py-2.5 text-xs
                ${priceChange.direction === "decrease" ? "text-emerald-700 bg-emerald-50" : "text-amber-800 bg-amber-50"}`}>
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {priceChange.direction === "decrease" ? "You save" : "Increase"}
                </span>
                <span className="font-mono font-bold">
                  {priceChange.delta > 0 ? "+" : ""}{priceChange.currency} {Math.abs(Number(priceChange.delta)).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPriceChange(null)} data-testid="price-change-cancel-btn">
              Not now
            </Button>
            <Button onClick={confirmNewPrice} disabled={loading}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)]"
                    data-testid="price-change-confirm-btn">
              {loading ? "Continuing…" : `Confirm ${priceChange?.currency} ${Number(priceChange?.new_total || 0).toFixed(2)} & pay`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
